import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Collapse,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  App,
} from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import {
  CheckCircleOutlined,
  CheckSquareOutlined,
  EditOutlined,
  HistoryOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import workOrderService, {
  type WorkOrderBoqOperationRow,
  type WorkOrderBoqLinePayload,
  type WorkOrderBoqLineDocumentsLine,
  type WorkOrderBoqStageSummary,
  type InlandDeviationPrefillResponse,
} from '@/services/workOrder.service';
import type { Item } from '@/services/inventory.service';
import { useItems } from '@/hooks/useInventory';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import WorkOrderStages from '@/components/qc/WorkOrderStages';
import { WoBoqLineTechnicalDocumentsPanel } from '@/components/manufacturing/WoBoqLineTechnicalDocumentsPanel';
import EodOperationWorkLogDrawer from '@/components/manufacturing/eod-close/EodOperationWorkLogDrawer';
import {
  useCheckWorkOrderBlocked,
  useCompleteStage,
  useRequestInspection,
  useResumeStage,
  useRevertStageToPending,
  useStartOutsourcedStageWithPr,
  useStartStage,
  useUpdateStage,
  useUsers,
  useWorkstations,
} from '@/hooks/useQCWorkflow';
import { buildUserIdToLabelMap, resolveUserDisplayFromMap } from '@/utils/userDisplay';
import { isFabricationExecutionScope } from '@/utils/executionScope';
import { useAuthStore } from '@/store/authStore';
import { stageStartAssignmentsRequiredMessage, hasRequiredStageStartAssignments } from '@/utils/stageStartAssignments';
const { Text, Paragraph } = Typography;

/** Fabrication outsourced PR UI copy (procurement grouped per BOQ op; execution stays unit-wise). */
const FABRICATION_OUTSOURCED_PR_HELP =
  'PR quantity uses total BOQ line quantity. One PR is created per outsourced operation for the BOQ line, not per production unit.';
const FABRICATION_UNIT_WISE_REMINDER =
  'Fabrication operations, inspections, progress, and delivery remain unit-wise.';

type BoqOpStageRow = { key: string; op: WorkOrderBoqOperationRow; stage: WorkOrderBoqStageSummary | null };

function stagesForBoqOp(op: WorkOrderBoqOperationRow): WorkOrderBoqStageSummary[] {
  if (Array.isArray(op.work_order_stages) && op.work_order_stages.length > 0) return op.work_order_stages;
  if (op.work_order_stage) return [op.work_order_stage];
  return [];
}

/** Another unit stage for the same BOQ operation already has an outsourced PR (BOQ-line-wise PR). */
function siblingStageHasOutsourcedPr(op: WorkOrderBoqOperationRow, currentStageId: string): boolean {
  return stagesForBoqOp(op).some((s) => s.outsourced_pr_id && s.id !== currentStageId);
}

const OUTSOURCE_TRACE_COLORS: Record<string, string> = {
  VendorPending: 'gold',
  ReceivedAwaitingInspection: 'cyan',
  InspectionPending: 'processing',
  Released: 'green',
  Rejected: 'error',
};

function outsourcedTraceBlocksActions(trace: string | null | undefined): boolean {
  return (
    trace === 'ReceivedAwaitingInspection' ||
    trace === 'InspectionPending' ||
    trace === 'Rejected'
  );
}

/** Returns null when no stages exist on any operation (flat table uses first stage slot only). */
function collectUnitKeysForLine(line: WorkOrderBoqLinePayload): Array<number | 'legacy'> | null {
  let hasLegacy = false;
  const unitNums = new Set<number>();
  for (const op of line.operations) {
    for (const st of stagesForBoqOp(op)) {
      if (st.production_unit_number == null || st.production_unit_number === undefined) {
        hasLegacy = true;
      } else {
        unitNums.add(Number(st.production_unit_number));
      }
    }
  }
  if (!hasLegacy && unitNums.size === 0) return null;
  const sorted = [...unitNums].sort((a, b) => a - b);
  const out: Array<number | 'legacy'> = [];
  if (hasLegacy) out.push('legacy');
  out.push(...sorted);
  return out;
}

function rowsForLineAndUnit(line: WorkOrderBoqLinePayload, unitKey: number | 'legacy'): BoqOpStageRow[] {
  return line.operations.map((op) => {
    const stages = stagesForBoqOp(op);
    const st =
      unitKey === 'legacy'
        ? stages.find((s) => s.production_unit_number == null || s.production_unit_number === undefined) ?? null
        : stages.find((s) => Number(s.production_unit_number) === unitKey) ?? null;
    return { key: `${op.id}-${st?.id ?? 'ns'}`, op, stage: st };
  });
}

function rowsWithoutUnitGrouping(line: WorkOrderBoqLinePayload): BoqOpStageRow[] {
  return line.operations.map((op) => {
    const stages = stagesForBoqOp(op);
    const st = stages[0] ?? null;
    return { key: `${op.id}-${st?.id ?? 'ns'}`, op, stage: st };
  });
}

function unitPanelLabel(unitKey: number | 'legacy'): React.ReactNode {
  if (unitKey === 'legacy') return <Text strong>Legacy (single unit)</Text>;
  return <Text strong>Unit {unitKey}</Text>;
}

function unwrapSupplierCategories<T>(res: unknown): T {
  const data = (res as { data?: unknown })?.data ?? res;
  return (data as { data?: T })?.data ?? (data as T);
}

interface SupplierCategoryRow {
  id: string;
  category_name: string;
}

interface OutsourcePrLineRow {
  key: string;
  item_id?: string;
  item_code?: string;
  item_description?: string;
  requested_quantity: number;
  uom?: string;
  uomOptions?: string[];
  estimated_unit_price?: number;
  specifications?: string;
}

function getItemUomOptions(item: Item): string[] {
  const uoms = [item.base_uom, item.purchase_uom, item.sales_uom].filter(Boolean) as string[];
  return [...new Set(uoms)];
}

export interface WorkOrderOperationsProps {
  workOrderId: string;
  workOrderNumber?: string;
}

/**
 * Item-wise BOQ multi-item operations when quotation→BOQ chain exists; otherwise per-line WorkOrderStages (same as legacy Stages tab).
 */
const WorkOrderOperations: React.FC<WorkOrderOperationsProps> = ({ workOrderId, workOrderNumber }) => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const canSeeWoBoqLineDocs = Boolean(user?.permissions?.includes('manufacturing.view'));

  const { data: payload, isLoading, isError, error } = useQuery({
    queryKey: ['work-orders', workOrderId, 'boq-operations'],
    queryFn: () => workOrderService.getBoqOperations(workOrderId),
    enabled: !!workOrderId?.trim(),
  });

  const { data: woBoqLineDocuments } = useQuery({
    queryKey: ['work-orders', workOrderId, 'boq-line-documents'],
    queryFn: () => workOrderService.getBoqLineDocuments(workOrderId),
    enabled: !!workOrderId?.trim() && canSeeWoBoqLineDocs,
    staleTime: 60_000,
  });

  const boqLineDocsByWoLineId = useMemo(() => {
    const m = new Map<string, WorkOrderBoqLineDocumentsLine>();
    for (const row of woBoqLineDocuments?.lines ?? []) {
      m.set(row.work_order_line_item_id, row);
    }
    return m;
  }, [woBoqLineDocuments]);

  const { data: inventoryItems = [] } = useItems({ is_active: true });

  const { data: supplierCategories = [] } = useQuery({
    queryKey: ['procurement-supplier-categories'],
    queryFn: async () => {
      const res = await api.get('/procurement/supplier-categories');
      const rows = unwrapSupplierCategories<SupplierCategoryRow[]>(res);
      return Array.isArray(rows) ? rows : [];
    },
  });

  const categoryLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of supplierCategories) {
      m.set(c.id, c.category_name);
    }
    return m;
  }, [supplierCategories]);

  const { data: isBlockedData } = useCheckWorkOrderBlocked(workOrderId);
  const isWorkOrderBlocked = isBlockedData?.isBlocked ?? false;

  const { data: workstations = [] } = useWorkstations(true);
  const { data: mfgUsers = [] } = useUsers({ is_active: true });
  const userDisplayMap = useMemo(() => buildUserIdToLabelMap(mfgUsers), [mfgUsers]);
  const userSelectOptions = useMemo(
    () =>
      (mfgUsers as { id: string; first_name?: string; last_name?: string; email?: string; username?: string }[]).map(
        (u) => ({
          value: u.id,
          label: `${[u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username || u.email || 'Unknown user'}${u.email ? ` (${u.email})` : ''}`,
        })
      ),
    [mfgUsers]
  );
  const updateStageMutation = useUpdateStage();

  const startStageMutation = useStartStage();
  const startOutsourcedPrMutation = useStartOutsourcedStageWithPr();
  const completeStageMutation = useCompleteStage();
  const requestInspectionMutation = useRequestInspection();
  const resumeStageMutation = useResumeStage();
  const revertToPendingMutation = useRevertStageToPending();

  const invalidateBoq = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'boq-operations'] });
  }, [queryClient, workOrderId]);

  const [outsourceModalOpen, setOutsourceModalOpen] = useState(false);
  const [editingOp, setEditingOp] = useState<WorkOrderBoqOperationRow | null>(null);
  const [outsourceSaving, setOutsourceSaving] = useState(false);
  const [outsourceForm] = Form.useForm();
  const watchedOutsourced = Form.useWatch('isOutsourced', outsourceForm);

  const [startPrModalOpen, setStartPrModalOpen] = useState(false);
  const [startPrStageId, setStartPrStageId] = useState<string | null>(null);
  const [startPrForm] = Form.useForm();
  const [outsourcedPrLines, setOutsourcedPrLines] = useState<OutsourcePrLineRow[]>([]);

  const [deviationModalOpen, setDeviationModalOpen] = useState(false);
  const [deviationOperationId, setDeviationOperationId] = useState<string | null>(null);
  const [deviationPrefill, setDeviationPrefill] = useState<InlandDeviationPrefillResponse | null>(null);
  const [deviationOutsourceSnapshot, setDeviationOutsourceSnapshot] = useState<{
    outsourceCost?: number;
    outsourcedSupplierCategoryIds?: string[];
    outsourcedScopeDetail?: string | null;
    notes?: string;
  } | null>(null);
  const [deviationPrLines, setDeviationPrLines] = useState<OutsourcePrLineRow[]>([]);
  const [deviationForm] = Form.useForm();
  const [deviationSubmitting, setDeviationSubmitting] = useState(false);
  const [workLogStage, setWorkLogStage] = useState<{
    id: string;
    title: string;
    notes: string | null;
    actualHours: number | null;
  } | null>(null);

  const openOutsourceModal = useCallback(
    (r: WorkOrderBoqOperationRow) => {
      setEditingOp(r);
      outsourceForm.setFieldsValue({
        isOutsourced: r.is_outsourced,
        outsourceCost: r.outsource_cost ?? undefined,
        outsourcedSupplierCategoryIds: r.outsourced_supplier_category_ids ?? [],
        outsourcedScopeDetail: r.outsourced_scope_detail ?? undefined,
        notes: r.notes ?? '',
      });
      setOutsourceModalOpen(true);
    },
    [outsourceForm]
  );

  const submitOutsourceModal = useCallback(async () => {
    if (!editingOp) return;
    try {
      const v = await outsourceForm.validateFields();
      const switchingToOutsourced = !editingOp.is_outsourced && Boolean(v.isOutsourced);
      if (switchingToOutsourced) {
        const opId = editingOp.id;
        setOutsourceSaving(true);
        try {
          const pre = await workOrderService.getInlandDeviationPrefill(workOrderId, opId);
          setDeviationPrefill(pre);
          setDeviationOutsourceSnapshot({
            outsourceCost: v.outsourceCost ?? undefined,
            outsourcedSupplierCategoryIds: v.outsourcedSupplierCategoryIds,
            outsourcedScopeDetail:
              typeof v.outsourcedScopeDetail === 'string' ? v.outsourcedScopeDetail.trim() || null : null,
            notes: typeof v.notes === 'string' && v.notes.trim() ? v.notes.trim() : undefined,
          });
          setDeviationOperationId(opId);
          const defaultQty = pre.default_pr_lines?.work_order_planned_quantity ?? 1;
          const defaultUom = (pre.default_pr_lines?.work_order_uom || 'PCS').trim() || 'PCS';
          const desc = String(pre.boq_snapshot?.description ?? '');
          setDeviationPrLines([
            {
              key: `d-${Date.now()}`,
              requested_quantity: defaultQty,
              uom: defaultUom,
              uomOptions: [defaultUom],
              estimated_unit_price: pre.default_pr_lines?.outsource_cost_default ?? v.outsourceCost ?? undefined,
            },
          ]);
          deviationForm.setFieldsValue({
            pr_date: dayjs(),
            required_date: dayjs().add(7, 'day'),
            pr_header_notes: desc ? `${desc} — in-house to outsourced` : 'In-house to outsourced',
            justification_reason: '',
            justification_category: undefined,
          });
          setOutsourceModalOpen(false);
          setEditingOp(null);
          setDeviationModalOpen(true);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { message?: string } }; message?: string };
          message.error(err?.response?.data?.message || err?.message || 'Could not open deviation request');
        } finally {
          setOutsourceSaving(false);
        }
        return;
      }

      setOutsourceSaving(true);
      await workOrderService.updateBoqOperationOutsource(workOrderId, editingOp.id, {
        isOutsourced: Boolean(v.isOutsourced),
        outsourceCost: v.isOutsourced ? (v.outsourceCost ?? null) : null,
        outsourcedSupplierCategoryIds: v.isOutsourced ? v.outsourcedSupplierCategoryIds : undefined,
        outsourcedScopeDetail: v.isOutsourced
          ? (typeof v.outsourcedScopeDetail === 'string' ? v.outsourcedScopeDetail.trim() : '') || null
          : null,
        notes: typeof v.notes === 'string' && v.notes.trim() ? v.notes.trim() : undefined,
      });
      message.success('Operation updated; BOQ line costs were recalculated');
      setOutsourceModalOpen(false);
      setEditingOp(null);
      invalidateBoq();
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown; message?: string; response?: { data?: { message?: string } } };
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || err?.message || 'Update failed');
    } finally {
      setOutsourceSaving(false);
    }
  }, [editingOp, invalidateBoq, message, outsourceForm, workOrderId, deviationForm]);

  const addOutsourcedPrLine = useCallback(() => {
    setOutsourcedPrLines((prev) => [...prev, { key: `pr-line-${Date.now()}`, requested_quantity: 1, uom: 'PCS', uomOptions: ['PCS'] }]);
  }, []);

  const removeOutsourcedPrLine = useCallback((key: string) => {
    setOutsourcedPrLines((prev) => prev.filter((l) => l.key !== key));
  }, []);

  const updateOutsourcedPrLine = useCallback((key: string, field: keyof OutsourcePrLineRow, value: unknown) => {
    setOutsourcedPrLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  }, []);

  const onSelectOutsourcedPrItem = useCallback(
    (key: string, itemId: string) => {
      const item = inventoryItems.find((i: Item) => i.id === itemId);
      if (!item) return;
      const uomOptions = getItemUomOptions(item);
      const defaultUom = item.base_uom || item.purchase_uom || item.sales_uom || uomOptions[0] || 'PCS';
      setOutsourcedPrLines((prev) =>
        prev.map((l) =>
          l.key === key
            ? {
                ...l,
                item_id: item.id,
                item_code: item.item_code,
                item_description: item.item_name || item.item_description || item.item_code,
                uom: defaultUom,
                uomOptions: uomOptions.length ? uomOptions : [defaultUom],
              }
            : l
        )
      );
    },
    [inventoryItems]
  );

  const clearOutsourcedPrLineItem = useCallback((key: string) => {
    setOutsourcedPrLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? { ...l, item_id: undefined, item_code: undefined, item_description: undefined, uom: undefined, uomOptions: undefined }
          : l
      )
    );
  }, []);

  const addDeviationPrLine = useCallback(() => {
    setDeviationPrLines((prev) => [
      ...prev,
      { key: `d-${Date.now()}`, requested_quantity: 1, uom: 'PCS', uomOptions: ['PCS'] },
    ]);
  }, []);

  const removeDeviationPrLine = useCallback((key: string) => {
    setDeviationPrLines((prev) => prev.filter((l) => l.key !== key));
  }, []);

  const updateDeviationPrLine = useCallback((key: string, field: keyof OutsourcePrLineRow, value: unknown) => {
    setDeviationPrLines((prev) => prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)));
  }, []);

  const onSelectDeviationPrItem = useCallback(
    (key: string, itemId: string) => {
      const item = inventoryItems.find((i: Item) => i.id === itemId);
      if (!item) return;
      const uomOptions = getItemUomOptions(item);
      const defaultUom = item.base_uom || item.purchase_uom || item.sales_uom || uomOptions[0] || 'PCS';
      setDeviationPrLines((prev) =>
        prev.map((l) =>
          l.key === key
            ? {
                ...l,
                item_id: item.id,
                item_code: item.item_code,
                item_description: item.item_name || item.item_description || item.item_code,
                uom: defaultUom,
                uomOptions: uomOptions.length ? uomOptions : [defaultUom],
              }
            : l
        )
      );
    },
    [inventoryItems]
  );

  const clearDeviationPrLineItem = useCallback((key: string) => {
    setDeviationPrLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? { ...l, item_id: undefined, item_code: undefined, item_description: undefined, uom: undefined, uomOptions: undefined }
          : l
      )
    );
  }, []);

  const submitDeviationForApproval = useCallback(async () => {
    if (!deviationOperationId || !deviationOutsourceSnapshot) return;
    try {
      const v = await deviationForm.validateFields();
      const validLines = deviationPrLines.filter(
        (l) => l.item_id && Number(l.requested_quantity) > 0 && String(l.uom || '').trim().length > 0
      );
      if (validLines.length === 0) {
        message.error('Add at least one PR line with an inventory item, quantity and UOM.');
        return;
      }
      setDeviationSubmitting(true);
      const created = await workOrderService.createInlandProcessDeviation(workOrderId, deviationOperationId, {
        proposed_outsource_cost: deviationOutsourceSnapshot.outsourceCost ?? null,
        proposed_supplier_category_ids: deviationOutsourceSnapshot.outsourcedSupplierCategoryIds,
        justification_reason: String(v.justification_reason || '').trim(),
        justification_category: v.justification_category ? String(v.justification_category) : undefined,
        pr_payload: {
          pr_date: (v.pr_date as dayjs.Dayjs).format('YYYY-MM-DD'),
          required_date: (v.required_date as dayjs.Dayjs).format('YYYY-MM-DD'),
          department_id: v.department_id,
          notes: v.pr_header_notes?.trim() || undefined,
          lines: validLines.map((l) => ({
            item_id: l.item_id!,
            requested_quantity: Number(l.requested_quantity),
            uom: String(l.uom || '').trim(),
            estimated_unit_price: l.estimated_unit_price,
            specifications:
              l.specifications?.trim() ||
              deviationOutsourceSnapshot.outsourcedScopeDetail?.trim() ||
              undefined,
          })),
        },
      });
      await workOrderService.submitInlandProcessDeviation(created.id);
      message.success('Deviation submitted for approval. Configure workflow entity type inland_process_deviation if needed.');
      setDeviationModalOpen(false);
      setDeviationOperationId(null);
      setDeviationPrefill(null);
      setDeviationOutsourceSnapshot(null);
      setDeviationPrLines([]);
      deviationForm.resetFields();
      invalidateBoq();
      navigate(`/growth/inland-process-deviations/${created.id}`);
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown; response?: { data?: { message?: string } }; message?: string };
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || err?.message || 'Submit failed');
    } finally {
      setDeviationSubmitting(false);
    }
  }, [
    deviationOperationId,
    deviationOutsourceSnapshot,
    deviationForm,
    deviationPrLines,
    workOrderId,
    message,
    invalidateBoq,
    navigate,
  ]);

  const submitStartPrModal = useCallback(async () => {
    if (!startPrStageId) return;
    try {
      const v = await startPrForm.validateFields();
      const validLines = outsourcedPrLines.filter(
        (l) => l.item_id && Number(l.requested_quantity) > 0 && String(l.uom || '').trim().length > 0
      );
      if (validLines.length === 0) {
        message.error('Add at least one line: select an inventory item, quantity and UOM (same as Create Purchase Request).');
        return;
      }
      await startOutsourcedPrMutation.mutateAsync({
        id: startPrStageId,
        body: {
          pr_date: (v.pr_date as dayjs.Dayjs).format('YYYY-MM-DD'),
          required_date: (v.required_date as dayjs.Dayjs).format('YYYY-MM-DD'),
          department_id: v.department_id,
          notes: v.pr_header_notes?.trim() || undefined,
          lines: validLines.map((l) => ({
            item_id: l.item_id!,
            requested_quantity: Number(l.requested_quantity),
            uom: String(l.uom || '').trim(),
            estimated_unit_price: l.estimated_unit_price,
            specifications: l.specifications?.trim() || undefined,
          })),
        },
      });
      setStartPrModalOpen(false);
      setStartPrStageId(null);
      setOutsourcedPrLines([]);
      startPrForm.resetFields();
      invalidateBoq();
    } catch (e: unknown) {
      const err = e as { errorFields?: unknown };
      if (err?.errorFields) return;
    }
  }, [
    startPrStageId,
    startPrForm,
    startOutsourcedPrMutation,
    invalidateBoq,
    outsourcedPrLines,
    message,
  ]);

  const handleStartClick = useCallback(
    async (row: BoqOpStageRow) => {
      const r = row.op;
      const st = row.stage;
      if (!st?.id) return;
      if (isWorkOrderBlocked) {
        message.error('Cannot start: Job Inspection must be approved first');
        return;
      }
      const assignmentMsg = stageStartAssignmentsRequiredMessage(st);
      if (assignmentMsg) {
        message.error(assignmentMsg);
        return;
      }
      if (r.is_outsourced && st.status === 'Pending' && !st.outsourced_pr_id) {
        if (siblingStageHasOutsourcedPr(r, st.id)) {
          message.error(
            `${FABRICATION_OUTSOURCED_PR_HELP} Open the existing PR from the unit stage that was started first.`
          );
          return;
        }
        setStartPrStageId(st.id);
        const defaultDesc = `${r.description}${workOrderNumber ? ` (${workOrderNumber} / ${st.stage_code})` : ''}`;
        const defaultQty = r.work_order_planned_quantity ?? 1;
        const defaultUom = (r.work_order_uom || 'PCS').trim() || 'PCS';
        const scope = r.outsourced_scope_detail?.trim() || undefined;
        setOutsourcedPrLines([
          {
            key: `pr-line-${Date.now()}`,
            requested_quantity: defaultQty,
            uom: defaultUom,
            uomOptions: [defaultUom],
            estimated_unit_price: r.outsource_cost ?? undefined,
            specifications: scope,
          },
        ]);
        startPrForm.setFieldsValue({
          pr_date: dayjs(),
          required_date: dayjs().add(7, 'day'),
          pr_header_notes: defaultDesc,
          outsourced_scope_detail: scope || '',
        });
        setStartPrModalOpen(true);
        return;
      }
      try {
        await startStageMutation.mutateAsync({ id: st.id });
        invalidateBoq();
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [isWorkOrderBlocked, message, startStageMutation, invalidateBoq, startPrForm, workOrderNumber]
  );

  const handleCompleteStage = useCallback(
    async (stageId: string) => {
      try {
        await completeStageMutation.mutateAsync({ id: stageId });
        invalidateBoq();
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [completeStageMutation, invalidateBoq]
  );

  const handleRequestInspection = useCallback(
    async (stageId: string) => {
      try {
        await requestInspectionMutation.mutateAsync({ id: stageId });
        invalidateBoq();
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [requestInspectionMutation, invalidateBoq]
  );

  const handleResumeStage = useCallback(
    async (stageId: string) => {
      try {
        await resumeStageMutation.mutateAsync({ id: stageId });
        invalidateBoq();
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [resumeStageMutation, invalidateBoq]
  );

  const handleRevertToPending = useCallback(
    async (stageId: string) => {
      try {
        await revertToPendingMutation.mutateAsync({ id: stageId });
        invalidateBoq();
      } catch (e: unknown) {
        console.error(e);
      }
    },
    [revertToPendingMutation, invalidateBoq]
  );

  const handleWorkstationChange = useCallback(
    async (row: BoqOpStageRow, workstationId: string | null) => {
      const st = row.stage;
      if (!st?.id) {
        message.warning('No work order stage is linked to this operation yet. Refresh the page or open the Stages tab.');
        return;
      }
      if (st.status !== 'Pending') {
        message.warning('Workstation can only be set while the operation is Pending (before Start).');
        return;
      }
      try {
        const ws = workstationId
          ? (workstations as { id: string; default_operator_user_id?: string | null }[]).find(
              (w) => w.id === workstationId
            )
          : undefined;
        const defaultOperatorId = ws?.default_operator_user_id ?? null;
        await updateStageMutation.mutateAsync({
          id: st.id,
          workOrderId,
          data: {
            workstation_id: workstationId,
            assigned_to_user_id: defaultOperatorId,
          },
        });
        invalidateBoq();
      } catch {
        // useUpdateStage shows error toast
      }
    },
    [workOrderId, updateStageMutation, invalidateBoq, message, workstations]
  );

  const handleStageUserRoleChange = useCallback(
    async (row: BoqOpStageRow, field: 'assigned_to_user_id' | 'inspector_user_id' | 'verifier_user_id', userId: string | null) => {
      const st = row.stage;
      if (!st?.id) {
        message.warning('No work order stage is linked to this operation yet. Refresh the page or open the Stages tab.');
        return;
      }
      if (st.status !== 'Pending') {
        message.warning('Operator, inspector, and verifier can only be changed while the operation is Pending (before Start).');
        return;
      }
      try {
        await updateStageMutation.mutateAsync({
          id: st.id,
          workOrderId,
          data: { [field]: userId },
        });
        invalidateBoq();
      } catch {
        // useUpdateStage shows error toast
      }
    },
    [workOrderId, updateStageMutation, invalidateBoq, message]
  );

  const statusColor = (status: string) => {
    const colors: Record<string, string> = {
      Pending: 'default',
      'In Progress': 'processing',
      Completed: 'success',
      'On Hold': 'warning',
      Skipped: 'default',
    };
    return colors[status] || 'default';
  };

  const makeBoqColumns = useCallback(
    (lineRevision?: string | null): ColumnsType<BoqOpStageRow> => [
      { title: 'Seq', key: 'seq', width: 56, render: (_: unknown, row) => row.op.sequence_number },
      {
        title: 'Description',
        key: 'desc',
        ellipsis: true,
        render: (_: unknown, row) => <Text ellipsis>{row.op.description}</Text>,
      },
      {
        title: 'Revision',
        key: 'revision',
        width: 72,
        render: () => (lineRevision ? <Text>{lineRevision}</Text> : <Text type="secondary">—</Text>),
      },
      {
        title: 'Setup (h)',
        key: 'su',
        width: 88,
        render: (_: unknown, row) => Number(row.op.setup_time_hours ?? 0).toFixed(2),
      },
      {
        title: 'Run/u',
        key: 'run',
        width: 88,
        render: (_: unknown, row) => Number(row.op.run_time_per_unit_hours ?? 0).toFixed(4),
      },
      {
        title: 'Est. runtime (h)',
        key: 'estRuntime',
        width: 110,
        render: (_: unknown, row) => {
          const h =
            row.stage?.estimated_hours ??
            row.op.estimated_runtime_hours ??
            null;
          if (h == null || !Number.isFinite(Number(h))) return '—';
          return Number(h).toFixed(2);
        },
      },
      {
        title: 'Actual (h)',
        key: 'actRuntime',
        width: 100,
        render: (_: unknown, row) => {
          const fromStage = row.stage?.actual_hours;
          const fromOp = row.op.actual_hours;
          // Prefer this unit's stage when fabricating unit-wise rows; else op total.
          const h =
            fromStage != null && Number.isFinite(Number(fromStage))
              ? Number(fromStage)
              : fromOp != null && Number.isFinite(Number(fromOp))
                ? Number(fromOp)
                : null;
          if (h == null) return '—';
          return h.toFixed(2);
        },
      },
      {
        title: 'Labor/h',
        key: 'lab',
        width: 80,
        render: (_: unknown, row) => Number(row.op.labor_rate_per_hour ?? 0).toFixed(2),
      },
      {
        title: 'Execution type',
        key: 'execType',
        width: 128,
        render: (_: unknown, row) =>
          row.op.is_outsourced ? (
            <Tag color="orange">Outsourced</Tag>
          ) : (
            <Tag color="blue">In-house</Tag>
          ),
      },
      {
        title: 'Procurement categories',
        key: 'procCat',
        width: 220,
        ellipsis: true,
        render: (_: unknown, row) => {
          if (!row.op.is_outsourced) return <Text type="secondary">—</Text>;
          const ids = row.op.outsourced_supplier_category_ids ?? [];
          if (ids.length === 0) return <Text type="secondary">—</Text>;
          return (
            <Space size={[4, 4]} wrap>
              {ids.map((id) => (
                <Tag key={id}>{categoryLabelById.get(id) ?? id.slice(0, 8)}</Tag>
              ))}
            </Space>
          );
        },
      },
      {
        title: 'Outsourced scope',
        key: 'outsourcedScope',
        width: 180,
        ellipsis: true,
        render: (_: unknown, row) => {
          if (!row.op.is_outsourced) return <Text type="secondary">—</Text>;
          const scope = row.op.outsourced_scope_detail?.trim();
          return scope ? (
            <Text ellipsis={{ tooltip: scope }}>{scope}</Text>
          ) : (
            <Text type="secondary">—</Text>
          );
        },
      },
      {
        title: 'Stage',
        key: 'st',
        width: 120,
        render: (_: unknown, row) =>
          row.stage ? (
            <Space size={4} direction="vertical">
              <Tag color={statusColor(row.stage.status)}>{row.stage.status}</Tag>
              {row.stage.outsourced_trace_status ? (
                <Tag color={OUTSOURCE_TRACE_COLORS[row.stage.outsourced_trace_status] ?? 'default'}>
                  {row.stage.outsourced_trace_status}
                </Tag>
              ) : null}
            </Space>
          ) : (
            <Text type="secondary">No stage</Text>
          ),
      },
      {
        title: 'Workstation',
        key: 'workstation',
        width: 240,
        render: (_: unknown, row) => {
          const st = row.stage;
          if (!st) {
            return <Text type="secondary">No stage</Text>;
          }
          const canSet = st.status === 'Pending';
          const options = (workstations as { id: string; workstation_code: string; workstation_name: string }[]).map(
            (ws) => ({
              value: ws.id,
              label: `${ws.workstation_code} — ${ws.workstation_name}`,
            })
          );
          const wsFromList = st.workstation_id
            ? (workstations as { id: string; workstation_code: string; workstation_name: string }[]).find(
                (w) => w.id === st.workstation_id
              )
            : undefined;
          const displayLine =
            wsFromList != null
              ? `${wsFromList.workstation_code} — ${wsFromList.workstation_name}`
              : st.workstation_name || st.workstation_id || '';
          const showBoldRunning =
            Boolean(st.workstation_id) &&
            (st.status === 'In Progress' || st.status === 'Paused' || st.status === 'On Hold');
          if (!canSet) {
            if (showBoldRunning && displayLine) {
              return (
                <Text strong style={{ fontSize: 14 }}>
                  Running on {displayLine}
                </Text>
              );
            }
            if (st.workstation_id) {
              return <Text type="secondary">{displayLine || st.workstation_id}</Text>;
            }
            return <Text type="secondary">—</Text>;
          }
          return (
            <Select
              showSearch
              allowClear
              placeholder="Select workstation"
              style={{ minWidth: 200, maxWidth: 280 }}
              value={st.workstation_id || undefined}
              options={options}
              optionFilterProp="label"
              onChange={(v) => void handleWorkstationChange(row, v ?? null)}
            />
          );
        },
      },
      {
        title: 'Operator',
        key: 'op',
        width: 200,
        render: (_: unknown, row) => {
          const st = row.stage;
          if (!st) return <Text type="secondary">No stage</Text>;
          const canSet = st.status === 'Pending';
          const v = (st.assigned_to_user_id || undefined) as string | undefined;
          const label =
            st.operator_name && String(st.operator_name).trim()
              ? String(st.operator_name).trim()
              : st.operator_email || resolveUserDisplayFromMap(userDisplayMap, v, '—');
          if (!canSet) {
            return label ? <Text type="secondary">{label}</Text> : <Text type="secondary">—</Text>;
          }
          return (
            <Select
              showSearch
              allowClear
              placeholder="Operator"
              style={{ minWidth: 160, maxWidth: 200 }}
              value={v}
              options={userSelectOptions}
              optionFilterProp="label"
              onChange={(x) => void handleStageUserRoleChange(row, 'assigned_to_user_id', x ? String(x) : null)}
            />
          );
        },
      },
      {
        title: 'Inspector',
        key: 'insp',
        width: 200,
        render: (_: unknown, row) => {
          const st = row.stage;
          if (!st) return <Text type="secondary">No stage</Text>;
          const canSet = st.status === 'Pending';
          const v = (st.inspector_user_id || undefined) as string | undefined;
          const label =
            st.inspector_name && String(st.inspector_name).trim()
              ? String(st.inspector_name).trim()
              : st.inspector_email || resolveUserDisplayFromMap(userDisplayMap, v, '—');
          if (!canSet) {
            return label ? <Text type="secondary">{label}</Text> : <Text type="secondary">—</Text>;
          }
          return (
            <Select
              showSearch
              allowClear
              placeholder="Inspector"
              style={{ minWidth: 160, maxWidth: 200 }}
              value={v}
              options={userSelectOptions}
              optionFilterProp="label"
              onChange={(x) => void handleStageUserRoleChange(row, 'inspector_user_id', x ? String(x) : null)}
            />
          );
        },
      },
      {
        title: 'Verifier',
        key: 'verf',
        width: 200,
        render: (_: unknown, row) => {
          const st = row.stage;
          if (!st) return <Text type="secondary">No stage</Text>;
          const canSet = st.status === 'Pending';
          const v = (st.verifier_user_id || undefined) as string | undefined;
          const label =
            st.verifier_name && String(st.verifier_name).trim()
              ? String(st.verifier_name).trim()
              : st.verifier_email || resolveUserDisplayFromMap(userDisplayMap, v, '—');
          if (!canSet) {
            return label ? <Text type="secondary">{label}</Text> : <Text type="secondary">—</Text>;
          }
          return (
            <Select
              showSearch
              allowClear
              placeholder="Verifier"
              style={{ minWidth: 160, maxWidth: 200 }}
              value={v}
              options={userSelectOptions}
              optionFilterProp="label"
              onChange={(x) => void handleStageUserRoleChange(row, 'verifier_user_id', x ? String(x) : null)}
            />
          );
        },
      },
      {
        title: 'Started',
        key: 'startedAt',
        width: 168,
        render: (_: unknown, row) => {
          const d = row.stage?.started_date;
          if (!d) return <Text type="secondary">—</Text>;
          const dt = new Date(d);
          return Number.isNaN(dt.getTime())
            ? String(d)
            : dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
        },
      },
      {
        title: 'Completed',
        key: 'completedAt',
        width: 168,
        render: (_: unknown, row) => {
          const d = row.stage?.completed_date;
          if (!d) return <Text type="secondary">—</Text>;
          const dt = new Date(d);
          return Number.isNaN(dt.getTime())
            ? String(d)
            : dt.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
        },
      },
      {
        title: 'PR',
        key: 'prLink',
        width: 88,
        render: (_: unknown, row) => {
          const prId = row.stage?.outsourced_pr_id;
          if (!prId) return <Text type="secondary">—</Text>;
          return (
            <Link to={`/scm/procurement/purchase-requests/${prId}`} onClick={(e) => e.stopPropagation()}>
              Open
            </Link>
          );
        },
      },
      {
        title: 'Actions',
        key: 'act',
        width: 300,
        fixed: 'right',
        render: (_: unknown, row) => {
          const r = row.op;
          const st = row.stage;
          const canEditExecution = !st || st.status === 'Pending' || st.status === 'On Hold';
          const startLoading = startStageMutation.isPending || startOutsourcedPrMutation.isPending;
          const outsourcedPrBlocked =
            Boolean(r.is_outsourced && st && !st.outsourced_pr_id && siblingStageHasOutsourcedPr(r, st.id));
          const outsourceTraceBlocked = outsourcedTraceBlocksActions(st?.outsourced_trace_status);
          const inspRequired = Boolean(st?.inspection_required);
          const inspStatus = (st?.inspection_status ?? '').trim();
          const canCompleteFromInProgress =
            st?.status === 'In Progress' && !inspRequired && !outsourceTraceBlocked;
          const canCompleteAfterInspection =
            st?.status === 'Inspection Pending' &&
            inspStatus === 'Approved' &&
            !outsourceTraceBlocked;
          return (
            <Space size="small" wrap>
              {st ? (
                <Tooltip
                  title={
                    canEditExecution
                      ? 'Change execution type (in-house vs outsourced), cost, and supplier categories'
                      : 'Execution type can only be changed while the stage is Pending or On Hold.'
                  }
                >
                  <span>
                    <Button
                      type="link"
                      size="small"
                      icon={<EditOutlined />}
                      disabled={!canEditExecution}
                      onClick={() => {
                        if (canEditExecution) openOutsourceModal(r);
                      }}
                    />
                  </span>
                </Tooltip>
              ) : null}
              {!st ? (
                <Text type="secondary">—</Text>
              ) : (
                <>
                  {st.status === 'Pending' && (
                    <Tooltip
                      title={
                        isWorkOrderBlocked
                          ? 'Job Inspection must be approved first'
                          : stageStartAssignmentsRequiredMessage(st) ||
                            (outsourcedPrBlocked
                              ? `${FABRICATION_OUTSOURCED_PR_HELP} ${FABRICATION_UNIT_WISE_REMINDER}`
                              : r.is_outsourced && !st.outsourced_pr_id
                                ? isFabricationExecutionScope(payload?.execution_scope)
                                  ? `Start with Purchase Request. ${FABRICATION_OUTSOURCED_PR_HELP}`
                                  : 'Start with Purchase Request (enter PR dates and details)'
                                : 'Start')
                      }
                    >
                      <Button
                        type="link"
                        size="small"
                        icon={<PlayCircleOutlined />}
                        disabled={
                          isWorkOrderBlocked ||
                          outsourcedPrBlocked ||
                          outsourceTraceBlocked ||
                          !hasRequiredStageStartAssignments(st)
                        }
                        loading={startLoading}
                        onClick={() => void handleStartClick(row)}
                      />
                    </Tooltip>
                  )}
                  {st.status === 'In Progress' && (
                    <>
                      <Tooltip title="Back to Pending">
                        <Popconfirm
                          title="Move back to Pending?"
                          description="Clears started time and the PR link on this stage. The purchase request (if any) is not deleted."
                          okText="Yes"
                          cancelText="No"
                          onConfirm={() => void handleRevertToPending(st.id)}
                        >
                          <Button
                            type="link"
                            size="small"
                            icon={<RollbackOutlined />}
                            loading={revertToPendingMutation.isPending}
                          />
                        </Popconfirm>
                      </Tooltip>
                      {inspRequired && (
                        <Tooltip title="Request stage completion inspection before marking this operation complete (required for this operation).">
                          <Button
                            type="link"
                            size="small"
                            icon={<CheckSquareOutlined />}
                            style={{ color: '#fa8c16' }}
                            loading={requestInspectionMutation.isPending}
                            onClick={() => void handleRequestInspection(st.id)}
                          />
                        </Tooltip>
                      )}
                      {canCompleteFromInProgress && (
                        <Tooltip title="Complete (no inspection required for this operation)">
                          <Button
                            type="link"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            style={{ color: '#52c41a' }}
                            loading={completeStageMutation.isPending}
                            onClick={() => handleCompleteStage(st.id)}
                          />
                        </Tooltip>
                      )}
                    </>
                  )}
                  {st.status === 'Inspection Pending' && (
                    <Space size={4} wrap align="center">
                      <Tag color={inspStatus === 'Approved' ? 'green' : 'orange'}>
                        {inspStatus === 'Approved' ? 'Inspection approved' : 'Waiting inspection'}
                      </Tag>
                      {canCompleteAfterInspection && (
                        <Tooltip title="Complete operation after inspection approval">
                          <Button
                            type="link"
                            size="small"
                            icon={<CheckCircleOutlined />}
                            style={{ color: '#52c41a' }}
                            loading={completeStageMutation.isPending}
                            onClick={() => handleCompleteStage(st.id)}
                          />
                        </Tooltip>
                      )}
                    </Space>
                  )}
                  {st.status === 'Inspection Rejected' && (
                    <Space size={4} wrap align="center">
                      <Tag color="red">Rework required</Tag>
                      <Tooltip title="Resume operation after addressing inspection rejection">
                        <Button
                          type="link"
                          size="small"
                          icon={<PlayCircleOutlined />}
                          loading={resumeStageMutation.isPending}
                          onClick={() => void handleResumeStage(st.id)}
                        />
                      </Tooltip>
                    </Space>
                  )}
                  <Tooltip title="View operation work log">
                    <Button
                      type="link"
                      size="small"
                      icon={<HistoryOutlined />}
                      onClick={() =>
                        setWorkLogStage({
                          id: st.id,
                          title: `${st.stage_code} — ${st.stage_name}`,
                          notes: st.notes ?? null,
                          actualHours:
                            st.actual_hours != null && Number.isFinite(Number(st.actual_hours))
                              ? Number(st.actual_hours)
                              : null,
                        })
                      }
                    />
                  </Tooltip>
                </>
              )}
            </Space>
          );
        },
      },
    ],
    [
      isWorkOrderBlocked,
      startStageMutation.isPending,
      startOutsourcedPrMutation.isPending,
      completeStageMutation.isPending,
      requestInspectionMutation.isPending,
      resumeStageMutation.isPending,
      revertToPendingMutation.isPending,
      handleStartClick,
      handleCompleteStage,
      handleRequestInspection,
      handleResumeStage,
      handleRevertToPending,
      handleWorkstationChange,
      handleStageUserRoleChange,
      userSelectOptions,
      userDisplayMap,
      workstations,
      categoryLabelById,
      openOutsourceModal,
      payload?.execution_scope,
    ]
  );

  if (!workOrderId?.trim()) {
    return (
      <Alert type="warning" showIcon message="No work order selected" description="Open a work order to view operations." />
    );
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message="Could not load operations"
        description={(error as Error)?.message || 'Check network and permissions.'}
      />
    );
  }

  if (!payload?.lines?.length) {
    return (
      <Alert
        type="info"
        showIcon
        message="No line items"
        description={
          workOrderNumber ? (
            <>
              Work order <Text code>{workOrderNumber}</Text> has no line items.
            </>
          ) : (
            'This work order has no line items.'
          )
        }
      />
    );
  }

  if (!payload.has_boq_chain) {
    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }} data-work-order-id={workOrderId}>
        <Alert
          type="info"
          showIcon
          message="No BOQ chain on this work order"
          description="Operations are managed with work order stages (same as the previous Stages tab): add stages from masters, start/complete, and dimensions per line item."
        />
        {payload.lines.map((line) => (
          <Card
            key={line.work_order_line_item_id}
            size="small"
            title={
              <Space wrap>
                <Text strong>Line {line.line_number}</Text>
                {line.item_code ? <Text code>{line.item_code}</Text> : null}
                <Text type="secondary">{line.item_description}</Text>
                {line.revision ? <Tag>{line.revision}</Tag> : null}
              </Space>
            }
          >
            <WoBoqLineTechnicalDocumentsPanel
              compact
              canView={canSeeWoBoqLineDocs}
              row={boqLineDocsByWoLineId.get(line.work_order_line_item_id)}
            />
            <div style={{ marginTop: 12 }}>
              <WorkOrderStages workOrderId={workOrderId} lineItemId={line.work_order_line_item_id} />
            </div>
          </Card>
        ))}
      </Space>
    );
  }

  const isFabrication = isFabricationExecutionScope(payload.execution_scope);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }} data-work-order-id={workOrderId}>
      <Alert
        type="info"
        showIcon
        message="BOQ operations (per line item)"
        description={
          <>
            Routing comes from the quotation&apos;s BOQ.{' '}
            {isFabrication
              ? `${FABRICATION_UNIT_WISE_REMINDER} Partial delivery is allowed when units complete.`
              : 'Non-Fabrication: BOQ-line total quantity — complete all operations on a line before delivery (no partial delivery).'}{' '}
            For <Text strong>outsourced</Text> operations, pressing Start
            opens a dialog like Direct PR: inventory lines (qty/UOM), dates, and header notes; a draft PR is created and the
            stage moves to In Progress.
            {isFabrication ? (
              <>
                {' '}
                {FABRICATION_OUTSOURCED_PR_HELP}
              </>
            ) : null}{' '}
            In-house operations start without a PR. Use the pencil to change execution type, cost, and
            categories only while the stage is <Text strong>Pending</Text> or <Text strong>On Hold</Text>.
            You can also <Text strong>Add Stage</Text> below each line for extra operations beyond the BOQ
            routing.
            {payload.boq_id ? (
              <>
                {' '}
                <Link to={`/growth/estimations/boq/edit/${payload.boq_id}`}>Open BOQ</Link>
              </>
            ) : null}
          </>
        }
      />
      <Space wrap>
        {payload.boq_id ? (
          <Button size="small" onClick={() => navigate(`/growth/estimations/boq/edit/${payload.boq_id}`)}>
            Edit BOQ
          </Button>
        ) : null}
      </Space>
      <Collapse
        defaultActiveKey={payload.lines.map((l) => l.work_order_line_item_id)}
        items={payload.lines.map((line) => ({
          key: line.work_order_line_item_id,
          label: (
            <Space wrap>
              <Text strong>Line {line.line_number}</Text>
              {line.item_code ? <Text code>{line.item_code}</Text> : null}
              <Text>{line.item_description}</Text>
              {line.revision ? <Tag>{line.revision}</Tag> : null}
              {(() => {
                const n = boqLineDocsByWoLineId.get(line.work_order_line_item_id)?.attachments?.length ?? 0;
                return n > 0 ? <Tag color="green">{n} doc{n === 1 ? '' : 's'}</Tag> : null;
              })()}
              {!line.boq_line_item_id ? (
                <Tag color="warning">No BOQ line mapping</Tag>
              ) : line.operations.length === 0 ? (
                <Tag>No operations on BOQ line</Tag>
              ) : null}
            </Space>
          ),
          children: (
            <>
              <WoBoqLineTechnicalDocumentsPanel
                compact
                canView={canSeeWoBoqLineDocs}
                row={boqLineDocsByWoLineId.get(line.work_order_line_item_id)}
              />
              {!line.boq_line_item_id ? (
                <Paragraph type="secondary" style={{ marginTop: 8 }}>
                  This customer order line is not linked to a BOQ line on the quotation. Use stage management below for
                  this line.
                </Paragraph>
              ) : null}
              {line.boq_line_item_id && line.operations.length > 0 ? (() => {
                const unitKeys = collectUnitKeysForLine(line);
                const renderTable = (data: BoqOpStageRow[]) => (
                  <Table<BoqOpStageRow>
                    rowKey="key"
                    size="small"
                    pagination={false}
                    columns={makeBoqColumns(line.revision)}
                    dataSource={data}
                    scroll={{ x: 'max-content' }}
                    style={{ marginTop: 8 }}
                  />
                );
                if (unitKeys == null) {
                  return renderTable(rowsWithoutUnitGrouping(line));
                }
                if (unitKeys.length === 1) {
                  return renderTable(rowsForLineAndUnit(line, unitKeys[0]!));
                }
                return (
                  <Collapse
                    size="small"
                    style={{ marginTop: 8 }}
                    defaultActiveKey={unitKeys.map((k) => (k === 'legacy' ? 'legacy' : String(k)))}
                    items={unitKeys.map((unitKey) => ({
                      key: unitKey === 'legacy' ? 'legacy' : String(unitKey),
                      label: unitPanelLabel(unitKey),
                      children: renderTable(rowsForLineAndUnit(line, unitKey)),
                    }))}
                  />
                );
              })() : null}
              <div style={{ marginTop: 12 }}>
                {line.boq_line_item_id && line.operations.length > 0 ? (
                  <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                    Additional stages (beyond BOQ operations) for this line:
                  </Paragraph>
                ) : null}
                <WorkOrderStages
                  workOrderId={workOrderId}
                  lineItemId={line.work_order_line_item_id}
                  manualStagesOnly={Boolean(line.boq_line_item_id && line.operations.length > 0)}
                />
              </div>
            </>
          ),
        }))}
      />
      <Modal
        title={editingOp ? `Operation — ${editingOp.description?.slice(0, 80) || editingOp.id}` : 'Operation'}
        open={outsourceModalOpen}
        onCancel={() => {
          setOutsourceModalOpen(false);
          setEditingOp(null);
        }}
        onOk={submitOutsourceModal}
        confirmLoading={outsourceSaving}
        destroyOnClose
        width={520}
      >
        <Form form={outsourceForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item
            name="isOutsourced"
            label="Execution"
            valuePropName="checked"
            tooltip="Stored on the BOQ line operation (source of truth for costing and this screen)."
          >
            <Switch checkedChildren="Outsourced" unCheckedChildren="In-house" />
          </Form.Item>
          {editingOp && !editingOp.is_outsourced && watchedOutsourced ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Inland Process Deviation required"
              description="You cannot switch to outsourced with a direct save. Continue to capture justification, BOQ cost snapshot, and planned PR lines; submission sends the request through universal approval. After approval, the operation becomes outsourced and a PR is created automatically when rules allow (e.g. Job Inspection approved)."
            />
          ) : null}
          {watchedOutsourced ? (
            <>
              <Form.Item name="outsourceCost" label="Outsource cost">
                <InputNumber min={0} style={{ width: '100%' }} placeholder="Optional" />
              </Form.Item>
              <Form.Item name="outsourcedSupplierCategoryIds" label="Supplier categories (shortlist)">
                <Select
                  mode="multiple"
                  allowClear
                  placeholder="Select categories"
                  options={supplierCategories.map((c) => ({
                    value: c.id,
                    label: c.category_name,
                  }))}
                />
              </Form.Item>
              <Form.Item
                name="outsourcedScopeDetail"
                label="Operation outsourced scope detail"
                rules={[{ required: true, message: 'Describe the outsourced scope of work' }]}
                help="Vendor-facing scope. Seeded into PR line specifications on start."
              >
                <Input.TextArea rows={3} placeholder="What the vendor must perform" />
              </Form.Item>
            </>
          ) : null}
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Inland Process Deviation — in-house to outsourced"
        open={deviationModalOpen}
        onCancel={() => {
          setDeviationModalOpen(false);
          setDeviationOperationId(null);
          setDeviationPrefill(null);
          setDeviationOutsourceSnapshot(null);
          setDeviationPrLines([]);
          deviationForm.resetFields();
        }}
        onOk={() => void submitDeviationForApproval()}
        confirmLoading={deviationSubmitting}
        destroyOnClose
        width={920}
        okText="Submit for approval"
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Review the BOQ snapshot, complete justification, and enter the same style of PR lines as when starting an outsourced
          operation. After final approval, the system applies outsourcing on the BOQ operation and creates the PR.
        </Paragraph>
        {deviationPrefill?.boq_snapshot ? (
          <Card size="small" title="BOQ operation snapshot" style={{ marginBottom: 12 }}>
            <pre style={{ margin: 0, fontSize: 11, maxHeight: 160, overflow: 'auto' }}>
              {JSON.stringify(deviationPrefill.boq_snapshot, null, 2)}
            </pre>
          </Card>
        ) : null}
        <Form form={deviationForm} layout="vertical">
          <Form.Item
            name="justification_reason"
            label="Reason for outsourcing"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.TextArea rows={3} placeholder="Why move this operation off the shop floor?" />
          </Form.Item>
          <Form.Item name="justification_category" label="Category">
            <Select
              allowClear
              placeholder="Select"
              options={[
                { value: 'capacity', label: 'Capacity' },
                { value: 'skill', label: 'Skill / capability' },
                { value: 'machine', label: 'Machine / equipment' },
                { value: 'urgency', label: 'Urgency' },
                { value: 'business', label: 'Business decision' },
                { value: 'other', label: 'Other' },
              ]}
            />
          </Form.Item>
          <Space wrap style={{ width: '100%' }} size="middle">
            <Form.Item name="pr_date" label="PR date" rules={[{ required: true, message: 'Required' }]} style={{ minWidth: 200 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="required_date" label="Required date" rules={[{ required: true, message: 'Required' }]} style={{ minWidth: 200 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
              style={{ minWidth: 260 }}
            >
              <DepartmentSelect style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item name="pr_header_notes" label="PR header notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Planned PR lines (after approval)</Text>
            <Button type="dashed" icon={<PlusOutlined />} size="small" style={{ marginLeft: 8 }} onClick={addDeviationPrLine}>
              Add line
            </Button>
          </div>
          <Table<OutsourcePrLineRow>
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={deviationPrLines}
            scroll={{ x: 'max-content' }}
            columns={
              [
                {
                  title: 'Item',
                  width: 280,
                  render: (_, r) => (
                    <ItemSelect
                      items={inventoryItems}
                      autoLoad={false}
                      placeholder="Select from inventory"
                      style={{ width: '100%' }}
                      value={r.item_id || undefined}
                      onChange={(v) => (v ? onSelectDeviationPrItem(r.key, String(v)) : clearDeviationPrLineItem(r.key))}
                    />
                  ),
                },
                {
                  title: 'Qty',
                  width: 100,
                  render: (_, r) => (
                    <InputNumber
                      min={0.0001}
                      value={r.requested_quantity}
                      onChange={(v) => updateDeviationPrLine(r.key, 'requested_quantity', v ?? 0)}
                      style={{ width: '100%' }}
                    />
                  ),
                },
                {
                  title: 'UOM',
                  width: 110,
                  render: (_, r) => {
                    const opts = r.uomOptions?.length ? r.uomOptions : r.uom ? [r.uom] : [];
                    return (
                      <Select
                        placeholder="UOM"
                        style={{ width: '100%' }}
                        value={r.uom || undefined}
                        onChange={(v) => updateDeviationPrLine(r.key, 'uom', v)}
                        options={opts.map((u) => ({ value: u, label: u }))}
                        allowClear={opts.length === 0}
                      />
                    );
                  },
                },
                {
                  title: 'Est. unit price',
                  width: 120,
                  render: (_, r) => (
                    <InputNumber
                      min={0}
                      value={r.estimated_unit_price}
                      onChange={(v) => updateDeviationPrLine(r.key, 'estimated_unit_price', v)}
                      style={{ width: '100%' }}
                      placeholder="Optional"
                    />
                  ),
                },
                {
                  title: '',
                  width: 72,
                  render: (_, r) => (
                    <Button
                      type="link"
                      danger
                      size="small"
                      disabled={deviationPrLines.length <= 1}
                      onClick={() => removeDeviationPrLine(r.key)}
                    >
                      Remove
                    </Button>
                  ),
                },
              ] as ColumnsType<OutsourcePrLineRow>
            }
          />
        </Form>
      </Modal>

      <Modal
        title="Start outsourced operation — Purchase Request"
        open={startPrModalOpen}
        onCancel={() => {
          setStartPrModalOpen(false);
          setStartPrStageId(null);
          setOutsourcedPrLines([]);
          startPrForm.resetFields();
        }}
        onOk={() => void submitStartPrModal()}
        confirmLoading={startOutsourcedPrMutation.isPending}
        destroyOnClose
        width={880}
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Lines work like <Text strong>Create Purchase Request → Direct</Text>: pick inventory items, quantities and UOMs.
          {isFabricationExecutionScope(payload?.execution_scope) ? (
            <>
              {' '}
              {FABRICATION_OUTSOURCED_PR_HELP} {FABRICATION_UNIT_WISE_REMINDER}
            </>
          ) : null}
          Operation context is pre-filled in <Text strong>PR header notes</Text> (shown on the PR header).
        </Paragraph>
        <Form form={startPrForm} layout="vertical">
          <Space wrap style={{ width: '100%' }} size="middle">
            <Form.Item name="pr_date" label="PR date" rules={[{ required: true, message: 'Required' }]} style={{ minWidth: 200 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="required_date" label="Required date" rules={[{ required: true, message: 'Required' }]} style={{ minWidth: 200 }}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              name="department_id"
              label="Department"
              rules={[{ required: true, message: 'Department is required' }]}
              style={{ minWidth: 260 }}
            >
              <DepartmentSelect style={{ width: '100%' }} />
            </Form.Item>
          </Space>
          <Form.Item
            name="pr_header_notes"
            label="PR header notes"
            tooltip="Saved on the Purchase Request header (Notes field), e.g. BOQ operation / WO context for approvers."
          >
            <Input.TextArea rows={3} placeholder="Context for the PR (pre-filled from the operation)" />
          </Form.Item>
          <Form.Item
            name="outsourced_scope_detail"
            label="Outsourced scope (from BOQ)"
            tooltip="Read-only copy of BOQ operation outsourced scope detail. Applied as line specifications when starting."
          >
            <Input.TextArea rows={3} readOnly placeholder="No scope recorded on the BOQ operation" />
          </Form.Item>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Lines</Text>
            <Button type="dashed" icon={<PlusOutlined />} size="small" style={{ marginLeft: 8 }} onClick={addOutsourcedPrLine}>
              Add line
            </Button>
          </div>
          <Table<OutsourcePrLineRow>
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={outsourcedPrLines}
            scroll={{ x: 'max-content' }}
            columns={
              [
                {
                  title: 'Item',
                  width: 280,
                  render: (_, r) => (
                    <ItemSelect
                      items={inventoryItems}
                      autoLoad={false}
                      placeholder="Select from inventory"
                      style={{ width: '100%' }}
                      value={r.item_id || undefined}
                      onChange={(v) => (v ? onSelectOutsourcedPrItem(r.key, String(v)) : clearOutsourcedPrLineItem(r.key))}
                    />
                  ),
                },
                {
                  title: 'Qty',
                  width: 100,
                  render: (_, r) => (
                    <InputNumber
                      min={0.0001}
                      value={r.requested_quantity}
                      onChange={(v) => updateOutsourcedPrLine(r.key, 'requested_quantity', v ?? 0)}
                      style={{ width: '100%' }}
                    />
                  ),
                },
                {
                  title: 'UOM',
                  width: 110,
                  render: (_, r) => {
                    const opts = r.uomOptions?.length ? r.uomOptions : r.uom ? [r.uom] : [];
                    return (
                      <Select
                        placeholder="UOM"
                        style={{ width: '100%' }}
                        value={r.uom || undefined}
                        onChange={(v) => updateOutsourcedPrLine(r.key, 'uom', v)}
                        options={opts.map((u) => ({ value: u, label: u }))}
                        allowClear={opts.length === 0}
                      />
                    );
                  },
                },
                {
                  title: 'Est. unit price',
                  width: 120,
                  render: (_, r) => (
                    <InputNumber
                      min={0}
                      value={r.estimated_unit_price}
                      onChange={(v) => updateOutsourcedPrLine(r.key, 'estimated_unit_price', v)}
                      style={{ width: '100%' }}
                      placeholder="Optional"
                    />
                  ),
                },
                {
                  title: '',
                  width: 72,
                  render: (_, r) => (
                    <Button type="link" danger size="small" disabled={outsourcedPrLines.length <= 1} onClick={() => removeOutsourcedPrLine(r.key)}>
                      Remove
                    </Button>
                  ),
                },
              ] as ColumnsType<OutsourcePrLineRow>
            }
          />
        </Form>
      </Modal>

      <EodOperationWorkLogDrawer
        open={!!workLogStage}
        onClose={() => setWorkLogStage(null)}
        stageId={workLogStage?.id ?? null}
        title={workLogStage?.title ?? ''}
        fallbackNotes={workLogStage?.notes ?? null}
        fallbackHours={workLogStage?.actualHours ?? null}
        workOrderId={workOrderId}
      />
    </Space>
  );
};

export default WorkOrderOperations;
